import React, { useState, useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../ui/button'

const MAX_REASON = 1000

export const REFUND_ERROR_MESSAGES = {
  REFUND_REQUEST_OPEN: 'A refund request is already open for this donation.',
  DONATION_DISPUTED: "This donation is under dispute and can't be refunded right now.",
  REFUND_AMOUNT_EXCEEDS_REMAINING: "That amount is more than what's left to refund.",
}

const dollars = (cents) => `$${(cents / 100).toFixed(2)}`

export default function RefundRequestModal({ donation, onClose, onSubmit }) {
  const remaining = donation.amount - (donation.refundedAmount || 0)
  const [mode, setMode] = useState('full')
  const [partial, setPartial] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const reasonRef = useRef(null)

  useEffect(() => { reasonRef.current?.focus() }, [])

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = reason.trim()
    if (!trimmed) {
      setError('Please enter a reason.')
      return
    }
    const payload = { reason: trimmed }
    if (mode === 'partial') {
      const value = Number(partial)
      if (!Number.isFinite(value) || value <= 0) {
        setError('Enter an amount greater than $0.')
        return
      }
      const cents = Math.round(value * 100)
      if (cents > remaining) {
        setError(REFUND_ERROR_MESSAGES.REFUND_AMOUNT_EXCEEDS_REMAINING)
        return
      }
      payload.amountCents = cents
    }
    setError(null)
    setSubmitting(true)
    try {
      await onSubmit(payload)
    } catch (err) {
      setError(REFUND_ERROR_MESSAGES[err?.code] || REFUND_ERROR_MESSAGES[err?.message] || err?.message || 'Failed to submit refund request')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="refund-modal-title">
      <form onSubmit={handleSubmit} className="bg-white rounded p-6 w-full max-w-md space-y-4">
        <h3 id="refund-modal-title" className="text-lg font-semibold text-charcoal">Request refund</h3>
        <p className="text-sm text-charcoal-muted">
          {donation.userName} · {dollars(donation.amount)} · {dollars(remaining)} refundable
        </p>
        <div className="flex gap-4 text-sm text-charcoal">
          <label className="flex items-center gap-1">
            <input type="radio" name="refund-mode" checked={mode === 'full'} onChange={() => setMode('full')} />
            Full
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" name="refund-mode" checked={mode === 'partial'} onChange={() => setMode('partial')} />
            Partial
          </label>
        </div>
        {mode === 'partial' && (
          <div>
            <label htmlFor="refund-amount" className="block text-sm text-charcoal mb-1">Amount ($)</label>
            <input
              id="refund-amount"
              type="number"
              step="0.01"
              min="0"
              value={partial}
              onChange={(e) => setPartial(e.target.value)}
              className="w-full border border-surface-border rounded px-3 py-2 text-sm"
            />
          </div>
        )}
        <div>
          <label htmlFor="refund-reason" className="block text-sm text-charcoal mb-1">Reason</label>
          <textarea
            id="refund-reason"
            ref={reasonRef}
            value={reason}
            maxLength={MAX_REASON}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full border border-surface-border rounded px-3 py-2 text-sm"
          />
          <p className="text-xs text-charcoal-muted mt-1">
            A Claritas administrator reviews every refund. Once approved, the amount (including fees) is withdrawn from your parish's Stripe balance.
          </p>
        </div>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Submit request
          </Button>
        </div>
      </form>
    </div>
  )
}
